import type { ImageInferenceLocationPreference, RemoteImageModelEditorPreference } from './image-generation-preferences';
import type { NaidanRpcRegistrationId } from '@/01-models/ids';
/**
 * Domain Definitions (Single Source of Truth)
 *
 * These models represent the definitive state used by the UI and business logic.
 * They are strictly decoupled from DTOs to prevent persistence details from
 * leaking into the core, ensuring structural integrity and preventing data
 * inconsistencies.
 */
import type { ToolConfig, ToolExecutionResult } from '@/01-models/tool';
import type { UiLocale } from '@/01-models/ui-locale';
import type {
  AttachmentId,
  BinaryObjectId,
  ChatGroupId,
  ChatId,
  MessageId,
  HostModelDirectoryId,
  ProviderProfileId,
  ToolCallId,
  VolumeId,
} from '@/01-models/ids';

// --- Domain Definitions (Business Logic Layer) ---

export type Role = 'user' | 'assistant' | 'system' | 'tool';
export type StorageType = 'local' | 'opfs' | 'memory';
export type ToolConfigPersistence = 'disabled' | 'enabled';
export type { UiLocale };

export type Reasoning = {
  effort: 'none' | 'low' | 'medium' | 'high' | undefined,
};

export type LmParameters = {
  temperature: number | undefined,
  topP: number | undefined,
  maxCompletionTokens: number | undefined,
  presencePenalty: number | undefined,
  frequencyPenalty: number | undefined,
  stop: string[] | undefined,
  reasoning: Reasoning,
};

/**
 * Not named 'DEFAULT' because 'DEFAULT' usually implies specific fallback values
 * (e.g., temperature: 0.7), whereas this object represents a purely 'EMPTY' state
 * where all optional fields are explicitly initialized as 'undefined'.
 */
export const EMPTY_LM_PARAMETERS: LmParameters = {
  temperature: undefined,
  topP: undefined,
  maxCompletionTokens: undefined,
  presencePenalty: undefined,
  frequencyPenalty: undefined,
  stop: undefined,
  reasoning: { effort: undefined },
};

export type SystemPrompt =
  | { behavior: 'override', content: string | null }
  | { behavior: 'append', content: string };

export type HttpEndpoint = {
  type: 'openai' | 'ollama',
  url: string,
  httpHeaders?: [string, string][],
};

export type TransformersJsEndpoint = {
  type: 'transformers_js',
};

export type LlamaCppBrowserEndpoint = {
  type: 'llama_cpp_browser',
};

export type BrowserProvidedLmEndpoint = {
  type: 'browser_provided_lm',
};

export type UnsupportedExperimentalEndpoint = {
  type: 'unsupported_experimental_endpoint',
  persistedType: string | undefined,
};

export type NaidanRpcEndpoint = { type: 'naidan_rpc', registrationId: NaidanRpcRegistrationId | undefined };

export type SupportedEndpoint = NaidanRpcEndpoint | HttpEndpoint | TransformersJsEndpoint | BrowserProvidedLmEndpoint | LlamaCppBrowserEndpoint;
export type Endpoint = SupportedEndpoint | UnsupportedExperimentalEndpoint;
export type EndpointType = SupportedEndpoint['type'];

export type SettingsTitleGeneration =
  | 'disabled'
  | {
      endpoint: 'same_scope',
      model: 'same_scope' | { id: string },
      lmParameters: 'same_scope' | LmParameters,
    }
  | {
      endpoint: Endpoint,
      model: { id: string },
      lmParameters: LmParameters,
    };

export type ScopedTitleGeneration = SettingsTitleGeneration | 'inherit';

export type MultimodalContent =
  | { type: 'text', text: string }
  | { type: 'image_url', image_url: { url: string } };

export interface ToolCall {
  id: ToolCallId,
  type: 'function',
  function: {
    name: string,
    arguments: string,
  },
}

/** A selected conversation path, without storage metadata or tree structure. */
export type ChatMessage =
  | { id: MessageId, role: 'user', parts: readonly UserMessageNode['parts'][number][] }
  | { id: MessageId, role: 'assistant', parts: readonly AssistantMessageNode['parts'][number][] }
  | { id: MessageId, role: 'system', parts: readonly SystemMessageNode['parts'][number][] }
  | { id: MessageId, role: 'tool', parts: readonly ToolMessageNode['parts'][number][] };

export interface AttachmentBase {
  id: AttachmentId, // Attachment ID
  binaryObjectId: BinaryObjectId, // Pointer to the immutable Binary Object
  originalName: string,
  mimeType: string,
  size: number,
  uploadedAt: number,
}

export type Attachment =
  | (AttachmentBase & { status: 'persisted' })
  | (AttachmentBase & { status: 'memory', blob: Blob })
  | (AttachmentBase & { status: 'missing' });

export type MessageNodeBase = {
  id: MessageId,
  // The time this history node was created, not the first or last generated token.
  createdAt: number,
  replies: MessageBranch,
};

export type UserMessageNode = MessageNodeBase & {
  role: 'user',
  modelId: undefined,
  lmParameters: LmParameters | undefined,
  parts: (
    | {
        type: 'text',
        text: string,
        completeness: 'complete' | 'partial',
      }
    | {
        type: 'attachment',
        attachment: Attachment,
      }
  )[],
};

export type AssistantMessageNode = MessageNodeBase & {
  role: 'assistant',
  /** Model selection used for this response, with the same contract as Chat.modelId. */
  modelId: string | undefined,
  lmParameters: LmParameters | undefined,
  parts: (
    | {
        type: 'reasoning',
        text: string,
        completeness: 'complete' | 'partial',
      }
    | {
        type: 'text',
        text: string,
        completeness: 'complete' | 'partial',
      }
    | {
        type: 'tool_call',
        toolCall: ToolCall,
      }
  )[],
  // Absence means no interruption was recorded, not proof of normal completion.
  interruption:
    | undefined
    | { type: 'cancelled' }
    | {
        type: 'error',
        // This text may contain the locale used when the error was recorded.
        // Changing the UI locale later does not retranslate the saved message.
        message: string,
      },
};

export type SystemMessageNode = MessageNodeBase & {
  role: 'system',
  modelId: undefined,
  lmParameters: undefined,
  parts: {
    type: 'text',
    text: string,
    completeness: 'complete' | 'partial',
  }[],
};

export type ToolMessageNode = MessageNodeBase & {
  role: 'tool',
  modelId: undefined,
  lmParameters: undefined,
  parts: {
    type: 'tool_result',
    result: ToolExecutionResult,
  }[],
};

export type MessageNode = UserMessageNode | AssistantMessageNode | SystemMessageNode | ToolMessageNode;

export type MessageBranch = {
  items: MessageNode[],
};

export interface CombinedToolCall {
  id: ToolCallId, // The toolCallId
  nodeId: MessageId, // The ToolMessageNode's ID
  call: ToolCall,
  result: ToolExecutionResult,
}

export interface Chat {
  id: ChatId,
  title: string | null,
  groupId?: ChatGroupId | null,
  root: MessageBranch,
  currentLeafId?: MessageId,

  createdAt: number,
  updatedAt: number,
  debugEnabled: boolean,

  endpoint?: Endpoint,
  /**
   * User-facing model reference for this endpoint, used for selection, display,
   * DTO persistence, and generation requests. For llama.cpp browser, preserve
   * the readable source-specific value, such as "hf.co/alice/Chat-GGUF:Q4" or
   * "host/MyModels1/alice/Chat-GGUF:Q4". Resolve files separately; file names,
   * exact file locators, and host registration IDs are not substitutes.
   */
  modelId?: string,
  titleGeneration?: ScopedTitleGeneration,
  originChatId?: ChatId,
  originMessageId?: MessageId,

  systemPrompt?: SystemPrompt,
  lmParameters?: LmParameters,
  mounts?: Mount[],
  toolConfigs?: ToolConfig[],
}

/**
 * Chat Metadata
 * Represents the configuration and state of a chat, excluding its messages.
 * Used for sidebar listing and lightweight synchronization.
 */
export interface ChatMeta {
  id: ChatId,
  title: string | null,
  groupId?: ChatGroupId | null,
  currentLeafId?: MessageId,
  createdAt: number,
  updatedAt: number,
  debugEnabled: boolean,
  endpoint?: Endpoint,
  /** Same model selection and persistence contract as Chat.modelId. */
  modelId?: string,
  titleGeneration?: ScopedTitleGeneration,
  originChatId?: ChatId,
  originMessageId?: MessageId,
  systemPrompt?: SystemPrompt,
  lmParameters?: LmParameters,
  mounts?: Mount[],
  toolConfigs?: ToolConfig[],
}

/**
 * Chat Content
 * Represents the actual conversation data (the message tree).
 */
export interface ChatContent {
  root: MessageBranch,
  currentLeafId?: MessageId,
}

export type ChatSummary = Pick<Chat, 'id' | 'title' | 'updatedAt' | 'groupId'>;

export type ChatSidebarItem =
  | { id: string, type: 'chat', chat: ChatSummary };

export interface ChatGroup {
  id: ChatGroupId,
  name: string,
  isCollapsed: boolean,
  items: ChatSidebarItem[], // Order is defined by array index
  updatedAt: number,

  endpoint?: Endpoint,
  /** Model selection inherited by chats, with the same contract as Chat.modelId. */
  modelId?: string,
  titleGeneration?: ScopedTitleGeneration,
  systemPrompt?: SystemPrompt,
  lmParameters?: LmParameters,
  mounts?: Mount[],
  toolConfigs?: ToolConfig[],
}

// Sidebar hierarchy - order is implicit by position in array
export type SidebarItem =
  | ChatSidebarItem
  | { id: string, type: 'chat_group', chatGroup: ChatGroup };

/**
 * Hierarchy (Source of Truth for Structure)
 * Used to persist the visual tree separate from data records.
 */
export interface HierarchyChatNode {
  type: 'chat',
  id: ChatId,
}

export interface HierarchyChatGroupNode {
  type: 'chat_group',
  id: ChatGroupId,
  chat_ids: ChatId[],
}

export type HierarchyNode = HierarchyChatNode | HierarchyChatGroupNode;

export interface Hierarchy {
  items: HierarchyNode[],
}

export interface BinaryObject {
  id: BinaryObjectId,
  mimeType: string,
  size: number,
  createdAt: number,
  name?: string,
}

/**
 * Volume — a storage container that can be made available to the AI as a folder.
 *
 * User-facing label: "Folder" (shown in the UI to avoid technical jargon).
 * All internal identifiers, function names, and field names use "volume".
 */
export type VolumeType = 'opfs' | 'host';

export interface Volume {
  id: VolumeId,
  name: string,
  type: VolumeType,
  createdAt: number,
}

export interface MountBase {
  mountPath: string,
  readOnly: boolean,
}

export interface MountVolume extends MountBase {
  type: 'volume',
  volumeId: VolumeId,
}

/**
 * Mount point configuration.
 * Extensible for future types like 'tmpfs'.
 */
export type Mount = MountVolume;

/**
 * Storage Snapshot
 * Represents a complete snapshot of the storage for migration or backup.
 */
export interface StorageSnapshot {
  structure: {
    settings: Settings,
    hierarchy: Hierarchy,
    chatMetas: ChatMeta[],
    chatGroups: ChatGroup[],
  },
  /**
   * Stream of heavy content (full message trees and binary attachments).
   * This is kept as a generator to manage memory efficiency.
   */
  // eslint-disable-next-line local-rules/enforce-dependency-directions -- TODO(dependency-direction): Move the storage snapshot contract into the storage service API.
  contentStream: AsyncGenerator<import('@/00-storage/00-dto/dto').MigrationChunkDto>,
}

/**
 * Provider Profile
 * Represents a reusable template/preset for connection settings.
 * Profiles are used to quickly populate global settings or chat overrides,
 * but are NOT directly referenced during runtime request resolution.
 */
export interface ProviderProfile {
  id: ProviderProfileId,
  name: string,
  endpoint: Endpoint,
  /** Default model selection, with the same contract as Chat.modelId. */
  defaultModelId?: string,
  /** Title-generation model selection, with the same contract as Chat.modelId. */
  titleModelId?: string,
  systemPrompt?: string,
  lmParameters?: LmParameters,
}

export type BrowserImageModelLocation =
  | { kind: 'temp', path: string }
  | { kind: 'opfs', path: string }
  | { kind: 'host', directoryId: HostModelDirectoryId, path: string };

export type BrowserImageModelSelection = {
  primary: { slot: 'model' | 'diffusion', location: BrowserImageModelLocation },
  components: {
    slot: 'vae' | 'clipL' | 'clipG' | 't5' | 'lm',
    choice: { kind: 'file', location: BrowserImageModelLocation } | { kind: 'none' },
  }[],
  loras: { location: BrowserImageModelLocation, enabled: 'enabled' | 'disabled', strength: number }[],
};

export type LlamaCppBrowserSettings = {
  visionBf16Storage?: 'legacy-expand' | 'retain-checked',
  contextTokens?: number,
  modelDownloadDestination?: { kind: 'opfs' } | { kind: 'host', directoryId: HostModelDirectoryId },
};

export type BrowserImageGenerationSettings = {
  width?: number,
  height?: number,
  seedMode?: 'random' | 'fixed',
  seed?: string,
  debug?: 'off' | 'on',
  historyPersistence?: 'enabled' | 'disabled',
  modelDownloadDestination?: { kind: 'opfs' } | { kind: 'host', directoryId: HostModelDirectoryId },
  imageDownload?: { format?: 'png' | 'webp' | 'jpeg', metadata?: 'include' | 'omit' },
  modelSelection?: BrowserImageModelSelection,
  inferenceLocation?: ImageInferenceLocationPreference,
  remoteModelEditors?: RemoteImageModelEditorPreference[],
  preview?: {
    enabled?: 'enabled' | 'disabled',
    mode?: 'projection' | 'vae',
    interval?: number,
    startStep?: number,
    maxEdge?: number,
  },
  keepPreviews?: 'enabled' | 'disabled',
  maxPreviews?: number,
  maxResults?: number,
  bf16WeightType?: 'f32' | 'f16',
};

/** Visible defaults for the browser image editor; absent settings remain absent on disk. */
export const DEFAULT_BROWSER_IMAGE_GENERATION_SETTINGS = {
  width: 256,
  height: 256,
  seedMode: 'random',
  seed: '42',
  debug: 'off',
  historyPersistence: 'enabled',
  modelDownloadDestination: { kind: 'opfs' },
  imageDownload: { format: 'png', metadata: 'omit' },
  modelSelection: undefined,
  inferenceLocation: { kind: 'local' },
  remoteModelEditors: [],
  preview: { enabled: 'disabled', mode: 'vae', interval: 2, startStep: 1, maxEdge: 256 },
  keepPreviews: 'enabled',
  maxPreviews: 16,
  maxResults: 20,
  bf16WeightType: 'f32',
} satisfies BrowserImageGenerationSettings;

export type LlamaCppServerTimingsMode = 'auto' | 'enabled' | 'disabled';

export interface Settings {
  endpoint: Endpoint,
  /** Default model selection, with the same contract as Chat.modelId. */
  defaultModelId?: string,
  titleGeneration: SettingsTitleGeneration,
  storageType: StorageType,
  providerProfiles: ProviderProfile[],
  mounts: Mount[],
  heavyContentAlertDismissed?: boolean,
  systemPrompt?: string,
  lmParameters?: LmParameters,
  experimental?: {
    locale?: UiLocale,
    /**
     * Only makes development behavior checks easier; it does not improve
     * security and MUST NOT be used for security. A late-applied CSP meta cannot
     * prevent loads that already occurred. NEVER use this value for feature
     * permissions or ON/OFF decisions. Undefined clears the saved setting.
     */
    metaCspForDevelopmentNotForSecurity?: string,
    chatBrowserModelPickerTab?: 'files' | 'linked-folders' | 'hugging-face' | 'saved',
    modelDiscoveryTab?: 'candidates' | 'hugging_face' | 'local_files',
    markdownRendering?: 'block_markdown' | 'monolithic_html',
    /**
     * Controls whether tool configuration changes are persisted.
     * Missing means enabled; an explicit disabled value opts out.
     *
     * This does not enable or disable any tool by itself. When disabled, tool
     * config changes can still affect the current runtime session, but they are
     * not written to persisted Settings, Chat Group, or Chat tool configs.
     */
    toolConfigPersistence?: ToolConfigPersistence,
    /** Global tool configuration. Missing keys use application defaults. */
    toolConfigs?: ToolConfig[],
    fakeLm?: 'disabled' | 'enabled',
    naidanRpc?: 'disabled' | 'enabled',
    /** Display and collection preferences only; generation measurements remain in memory. */
    generationSpeed?: {
      status?: 'enabled' | 'disabled',
      inlineStats?: 'on_demand' | 'always',
      llamaCppServerTimings?: LlamaCppServerTimingsMode,
    },
    sidebarSendMessageReorder?: 'disabled' | 'move_sent_chat',
    globalSearch?: {
      scope?: 'all' | 'current_thread' | 'title_only',
      roleFilter?: 'all' | 'user' | 'assistant',
      previewMode?: 'always' | 'peek' | 'disabled',
      previewContextSize?: number | 'full',
    },
    /** Only the selected model is persisted; live conversation/media stay in memory. */
    liveConversationModelId?: string,
    llamaCppBrowser?: LlamaCppBrowserSettings,
    browserImageGeneration?: BrowserImageGenerationSettings,
    readonly unreadable?: {
      readonly [key: string]: unknown,
    },
    hostModelDirectories?: { id: HostModelDirectoryId, name: string }[],
  },
}

export const DEFAULT_SETTINGS: Omit<Settings, 'storageType' | 'endpoint'> = {
  // Fresh settings only. Persisted/legacy settings retain their own reasoning values.
  titleGeneration: { endpoint: 'same_scope', model: 'same_scope', lmParameters: { ...EMPTY_LM_PARAMETERS, reasoning: { effort: 'none' } } },
  providerProfiles: [],
  mounts: [],
  heavyContentAlertDismissed: false,
};

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
